import Joi from 'joi';
import PropTypes from 'prop-types';
import _ from 'lodash';

import { getAffirmativeOptions, getAnomalySummaryOptions, getNoAnomalySummaryOptions, getPittingAnomalySummaryOptions, getPittingNoAnomalySummaryOptions } from '../../../utils/data';
import { AnomalySummary as AnomalySummaryModel } from '../../../utils/models';
import { matchPhrases } from '../../../utils/utilities';

import Form from '../../common/form';
import toastService from '../../../services/toastService';


interface FormProps {
	initialAnomalySummary: AnomalySummaryModel;
	onAdd: (summary: AnomalySummaryModel) => void;
}

interface FormState {
	data: AnomalySummaryModel;
	errors: object;
	loading: boolean;
	progress: number;
}

class PipingAnomalySummary extends Form {
	state = {
		data: {
			pipeBody: '',
			noOfPoints: 0,
			pitting: '',
			noOfPittingLocations: 0,
			flanges: '',
			noOfFlanges: 0,
			fasteners: '',
			valves: '',
			noOfValves: 0,
			corrosionUnderPipeSupport: '',
			noOfCups: 0,
			compositeWrapOnLine: 'no',
			noOfWrapPoints: 0,
			conditionOfSmallBorePipings: '',
			noOfSmallBorePipings: 0,
			others: '',
			description: '',
			extentOfCoatingBreakdown: '',
		},
		errors: {},
		loading: false,
		progress: 0,
	};

	schema = {
		pipeBody: Joi.string().min(3).required().label('Pipe Body Condition'),
		noOfPoints: Joi.number().min(0).required().label('Number of Points'),
		pitting: Joi.string().min(3).required().label('Pitting Condition'),
		noOfPittingLocations: Joi.any().when('pitting', {
			is: 'No Pitting',
			then: Joi.number().min(0).required().label('Number of Pitting Locations'),
			otherwise: Joi.number().min(1).required().label('Number of Pitting Locations'),
		}),
		flanges: Joi.string().min(3).required().label('Flange & Fasteners Condition'),
		noOfFlanges: Joi.number().min(0).required().label('Number of Flanges (Have Anomalies)'),
		fasteners: Joi.string().min(3).required().label('Pipe Support Condition'),
		valves: Joi.string().min(3).required().label('Valves Condition'),
		noOfValves: Joi.number().min(0).required().label('Number of Valves (Have Anomalies)'),
		corrosionUnderPipeSupport: Joi.string().min(3).required().label('Corrosion Under Pipe Support (CUPS)'),
		noOfCups: Joi.number().min(0).required().label('Number of CUPS (Have Anomalies)'),
		compositeWrapOnLine: Joi.string().required().label('Composite Wrap On Line'),
		noOfWrapPoints: Joi.any().when('compositeWrapOnLine', {
			is: 'yes',
			then: Joi.number().min(1).required().label('Number of Wrap Points'),
			otherwise: Joi.number().min(0).required().label('Number of Wrap Points'),
		}), 
		conditionOfSmallBorePipings: Joi.string().min(3).required().label('Small Bore Pipings Condition'),
		noOfSmallBorePipings: Joi.number().min(0).required().label('Number of Small Bore Pipings (Have Anomalies)'),
		others: Joi.string().required().label('Other (If any)'),
		description: Joi.string().min(3).required().label('Description'),
		extentOfCoatingBreakdown: Joi.string().min(3).required().label('Extent of Coating Breakdown'),
	};

	static propTypes = {
		hasAnomalies: PropTypes.string,
		initialAnomalySummary: PropTypes.object,
		onAdd: PropTypes.func,
		useNewFormat: PropTypes.bool,
	};

	getFlangesNotFound = () => {
		if (this.props.useNewFormat) {
			return true;
		}

		return NO_ANOMALY_STATUSES.includes(this.state.data.flanges);
	};
	
	getPipeBodyNotFound = () => {
		if (this.props.useNewFormat) {
			return true;
		}

		return NO_ANOMALY_STATUSES.includes(this.state.data.pipeBody);
	};
	
	getCompositeWrapNotFound = () => {
		return matchPhrases(this.state.data.compositeWrapOnLine, 'no');
	};
	
	getCupsNotFound = () => {
		if (this.props.useNewFormat) {
			return true;
		}

		return NO_ANOMALY_STATUSES.includes(this.state.data.corrosionUnderPipeSupport);
	};
	
	getPittingNotFound = () => {
		return matchPhrases(this.state.data.pitting, 'no pitting');
	};
	
	getValvesNotFound = () => {
		if (this.props.useNewFormat) {
			return true;
		}

		return NO_ANOMALY_STATUSES.includes(this.state.data.valves);
	};
	
	getBorePipingsNotFound = () => {
		if (this.props.useNewFormat) {
			return true;
		}

		return NO_ANOMALY_STATUSES.includes(this.state.data.conditionOfSmallBorePipings);
	};
	
	componentDidMount(){
		if (this.props.initialAnomalySummary) {
			return this.setState({ data: this.props.initialAnomalySummary });
		}
	}

	componentDidUpdate(
		prevProps: Readonly<FormProps>,
		prevState: Readonly<FormState>,
		snapshot?: any
	) {
			if (this.props.useNewFormat) {
				if (!_.isEqual(prevProps.initialAnomalySummary, this.props.initialAnomalySummary)) {
					this.setState({ data: this.props.initialAnomalySummary });
				}

				return;
			}

			if (prevState.data.compositeWrapOnLine !== this.state.data.compositeWrapOnLine) {
				const data: AnomalySummaryModel = { ...this.state.data };
				
				if (this.getCompositeWrapNotFound()) data.noOfWrapPoints = 0;
				else data.noOfWrapPoints = data.noOfWrapPoints || 1;
	
				this.setState({ data });
			}
	
			if (prevState.data.pitting !== this.state.data.pitting) {
				const data: AnomalySummaryModel = { ...this.state.data };
	
				if (this.getPittingNotFound()) data.noOfPittingLocations = 0
				else data.noOfPittingLocations = data.noOfPittingLocations || 1
	
				this.setState({ data });
			}

			if (prevState.data.pipeBody !== this.state.data.pipeBody) {
				const data: AnomalySummaryModel = { ...this.state.data };
				
				if (this.getPipeBodyNotFound()) data.noOfPoints = 0;
				else data.noOfPoints = data.noOfPoints || 1;
	
				this.setState({ data });
			}
	
			if (prevState.data.others !== this.state.data.others) {
				const data: AnomalySummaryModel = { ...this.state.data };
	
				if (matchPhrases(this.state.data.others, 'no')) data.description = 'NIL'
				else data.description = data.description ?? '';
	
				this.setState({ data });
			}
	
			if (prevState.data.flanges !== this.state.data.flanges) {
				const data: AnomalySummaryModel = { ...this.state.data };
	
				if (this.getFlangesNotFound()) data.noOfFlanges = 0
				else data.noOfFlanges = data.noOfFlanges || 1
	
				this.setState({ data });
			}
	
			if (prevState.data.conditionOfSmallBorePipings !== this.state.data.conditionOfSmallBorePipings) {
				const data: AnomalySummaryModel = { ...this.state.data };
	
				if (this.getBorePipingsNotFound()) data.noOfSmallBorePipings = 0;
				else data.noOfSmallBorePipings = data.noOfSmallBorePipings || 1;
	
				this.setState({ data });
			}
	
			if (prevState.data.corrosionUnderPipeSupport !== this.state.data.corrosionUnderPipeSupport) {
				const data: AnomalySummaryModel = { ...this.state.data };
	
				if (this.getCupsNotFound()) data.noOfCups = 0;
				else data.noOfCups = data.noOfCups || 1;
	
				this.setState({ data });
			}
	
			if (prevState.data.valves !== this.state.data.valves) {
				const data: AnomalySummaryModel = { ...this.state.data };
	
				if (this.getValvesNotFound()) data.noOfValves = 0;
				else data.noOfValves = data.noOfValves || 1;
	
				this.setState({ data });
			}
	}

	doSubmit = (): void => {
		this.props.onAdd(this.state.data);
		toastService.success('Anomaly Summary Saved Successfully!');
	};

	renderAnomalyOptions = () => {
		if (matchPhrases(this.props.hasAnomalies, 'yes') || matchPhrases(this.props.hasAnomalies, '')) {
			return getAnomalySummaryOptions();
		}

		return getNoAnomalySummaryOptions();
	};

	renderPittingAnomalyOptions = () => {
		if (matchPhrases(this.props.hasAnomalies, 'yes') || matchPhrases(this.props.hasAnomalies, '')) {
			return getPittingAnomalySummaryOptions();
		}

		return getPittingNoAnomalySummaryOptions();
	};

	renderCoatingBreakdownOptions = () => {
		if (matchPhrases(this.props.hasAnomalies, 'yes') || matchPhrases(this.props.hasAnomalies, '')) {
			return getAnomalySummaryOptions();
		}

		return getNoAnomalySummaryOptions();
	};

	render() {
		return (
			<form autoComplete="off" onSubmit={this.handleSubmit}>
				<div className="row">
					<div className="col">
						{this.renderSelect({
							name: 'pipeBody',
							placeholder: 'Select Pipe Body Condition',
							label: 'Pipe Body Condition',
							options: this.renderAnomalyOptions(),
							disabled: this.props.useNewFormat
						})}
					</div>
					
					<div className="col">
						{this.renderInput({
							type: 'number',
							name: 'noOfPoints',
							placeholder: 'Enter Number of Points',
							label: 'Number of Points',
							disabled: this.getPipeBodyNotFound(),
						})}
					</div>

					<div className="col">
						{this.renderSelect({
							name: 'pitting',
							placeholder: 'Select Pitting Condition',
							label: 'Pitting Condition',
							options: this.renderPittingAnomalyOptions(),
							style: {
								backgroundColor: '#ffff0f'
							}
						})}
					</div>
					
					<div className="col">
						{this.renderInput({
							type: 'number',
							name: 'noOfPittingLocations',
							placeholder: 'Enter Number of Pitting Locations',
							label: 'Number of Pitting Locations',
							disabled: this.getPittingNotFound(),
						})}
					</div>

					<div className="col">
						{this.renderSelect({
							name: 'flanges',
							placeholder: 'Select Flange & Fasteners Condition',
							label: 'Flange & Fasteners Condition',
							options: this.renderAnomalyOptions(),
							disabled: this.props.useNewFormat
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'number',
							name: 'noOfFlanges',
							placeholder: 'Enter Number of Flanges',
							label: 'Number of Flanges (Have Anomalies)',
							disabled: this.getFlangesNotFound(),
						})}
					</div>
				</div>

				<div className="divider my-4"></div>

				<div className="row">
					<div className="col">
						{this.renderSelect({
							name: 'valves',
							placeholder: 'Select Valves Condition',
							label: 'Valves Condition',
							options: this.renderAnomalyOptions(),
							disabled: this.props.useNewFormat
						})}
					</div>
					
					<div className="col">
						{this.renderInput({
							type: 'number',
							name: 'noOfValves',
							placeholder: 'Enter Number of Valves',
							label: 'Number of Valves (Have Anomalies)',
							disabled: this.getValvesNotFound(),
						})}
					</div>

					<div className="col">
						{this.renderSelect({
							name: 'corrosionUnderPipeSupport',
							placeholder: 'Select Corrosion Under Pipe Support (CUPS)',
							label: 'Corrosion Under Pipe Support (CUPS)',
							options: this.renderAnomalyOptions(),
							disabled: this.props.useNewFormat
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'number',
							name: 'noOfCups',
							placeholder: 'Enter Number of Cups',
							label: 'Number of Cups (Have Anomalies)',
							disabled: this.getCupsNotFound(),
						})}
					</div>

					<div className="col">
						{this.renderSelect({
							name: 'fasteners',
							placeholder: 'Select Pipe Support Condition',
							label: 'Pipe Support Condition',
							options: this.renderAnomalyOptions(),
							disabled: this.props.useNewFormat
						})}
					</div>
				</div>

				<div className="divider my-4"></div>

				<div className="row">
					<div className="col">
						{this.renderSelect({
							name: 'compositeWrapOnLine',
							placeholder: 'Has Composite Wrap On Line',
							label: 'Composite Wrap On Line',
							options: getAffirmativeOptions(),
							style: {
								backgroundColor: '#ffff0f'
							}
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'number',
							name: 'noOfWrapPoints',
							placeholder: 'Enter Number of Wrap Points',
							label: 'Number of Wrap Points',
							disabled: this.getCompositeWrapNotFound(),
						})}
					</div>

					<div className="col">
						{this.renderSelect({
							name: 'conditionOfSmallBorePipings',
							placeholder: 'Select Small Bore Pipings Condition',
							label: 'Small Bore Pipings Condition',
							options: this.renderAnomalyOptions(),
							disabled: this.props.useNewFormat
						})}
					</div>

					<div className="col">
						{this.renderInput({
							type: 'number',
							name: 'noOfSmallBorePipings',
							placeholder: 'Enter Number of Small Bore Pipings',
							label: 'Number of Small Bore Pipings (Have Anomalies)',
							disabled: this.getBorePipingsNotFound(),
						})}
					</div>

					<div className="col">
						{this.renderSelect({
							name: 'extentOfCoatingBreakdown',
							placeholder: 'Select Extent of Coating Breakdown',
							label: 'Extent of Coating Breakdown',
							options: this.renderCoatingBreakdownOptions(),
							style: {
								backgroundColor: '#ffff0f'
							}
						})}
					</div>
				</div>

				<div className="divider my-4"></div>

				<div className="row">
					<div className="col">
						{this.renderSelect({
							name: 'others',
							placeholder: 'Select Other Remark Present',
							label: 'Others (If any)',
							options: getAffirmativeOptions(),
							style: {
								backgroundColor: '#ffff0f'
							}
						})}
					</div>

					<div className="col">
						{this.renderTextArea({
							name: 'description',
							placeholder: 'Enter Description',
							label: 'Description',
							rows: '6',
							style: {
								backgroundColor: '#ffff0f'
							}
						})}
					</div>
				</div>

				<div className="divider mt-4"></div>

				<div className="d-flex justify-content-end">
					{this.renderButton('Save Summary Information')}
				</div>
			</form>
		);
	}
}

const NO_ANOMALY_STATUSES = getNoAnomalySummaryOptions().map((option) => option.id);
export default PipingAnomalySummary;
